import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ReminderButton } from "../components/ReminderComponents";
import WhatsAppThreatWidget from "../components/WhatsAppThreatWidget";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Search,
  Loader2,
  Container,
  Ship,
  MapPin,
  Clock,
  Calendar,
  Anchor,
  ArrowRight,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  Target,
  Mail,
  Tag,
  Train,
  Truck,
  Camera,
  Image,
  History,
  RefreshCw,
} from "lucide-react";


// Common destination ports for quick selection
const COMMON_PORTS = [
  { code: "KOPER", label: "Koper, Slovenia" },
  { code: "ROTTERDAM", label: "Rotterdam, NL" },
  { code: "HAMBURG", label: "Hamburg, DE" },
  { code: "ANTWERP", label: "Antwerp, BE" },
  { code: "TRIESTE", label: "Trieste, IT" },
  { code: "PIRAEUS", label: "Piraeus, GR" },
];

// Shipping carriers list
const SHIPPING_CARRIERS = [
  { code: "CMA_CGM", name: "CMA CGM" },
  { code: "MAERSK", name: "Maersk" },
  { code: "MSC", name: "MSC" },
  { code: "HAPAG_LLOYD", name: "Hapag-Lloyd" },
  { code: "COSCO", name: "COSCO" },
  { code: "EVERGREEN", name: "Evergreen" },
  { code: "OOCL", name: "OOCL" },
  { code: "YANG_MING", name: "Yang Ming" },
  { code: "ONE", name: "ONE (Ocean Network Express)" },
  { code: "ZIM", name: "ZIM" },
  { code: "OTHER", name: "Other / Unknown" },
];

export default function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "track");
  const [containerNumber, setContainerNumber] = useState("");
  const [carrier, setCarrier] = useState("");  // Selected carrier
  const [destinationPort, setDestinationPort] = useState("KOPER"); // Default to Koper
  const [transportType, setTransportType] = useState("port_to_port"); // "port_to_port" or "combined_transport"
  const [metransRef, setMetransRef] = useState("");
  const [finalDestination, setFinalDestination] = useState("");
  const [europacificRef, setEuropacificRef] = useState("");
  const [notificationEmails, setNotificationEmails] = useState("");
  const [loading, setLoading] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [showEvents, setShowEvents] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [showPhotos, setShowPhotos] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [error, setError] = useState(null);
  const [arrivedAtDestination, setArrivedAtDestination] = useState(false);
  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const { getAuthHeaders, isEuropacificUser, user } = useAuth();
  const navigate = useNavigate();

  // Fetch history when tab changes to history
  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory();
    }
  }, [activeTab]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${API_URL}/tracking/history`, {
        headers: getAuthHeaders(),
      });
      setHistory(response.data);
    } catch (error) {
      toast.error("Failed to fetch tracking history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleTrackAgain = (containerNum) => {
    setContainerNumber(containerNum);
    setActiveTab("track");
  };

  const fetchPhotos = async (containerNum) => {
    try {
      const response = await axios.get(`${API_URL}/photos/container/${containerNum}`, {
        headers: getAuthHeaders(),
      });
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error("Failed to fetch photos:", error);
      setPhotos([]);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!containerNumber.trim()) {
      toast.error("Please enter a container number");
      return;
    }

    setLoading(true);
    setTrackingData(null);
    setError(null);
    setArrivedAtDestination(false);
    setPhotos([]);

    try {
      // Parse notification emails
      const emailList = notificationEmails.split(/[,;\s]+/).filter(e => e.trim());
      
      const response = await axios.post(
        `${API_URL}/tracking/search`,
        { 
          container_number: containerNumber.trim(),
          carrier: carrier || null,  // Selected carrier
          destination_port: destinationPort || null,
          transport_type: transportType,
          metrans_ref: transportType === "combined_transport" ? metransRef : null,
          final_destination: transportType === "combined_transport" ? finalDestination : null,
          europacific_ref: europacificRef || null,
          notification_emails: emailList
        },
        { headers: getAuthHeaders() }
      );
      setTrackingData(response.data);
      
      // Fetch photos for this container
      await fetchPhotos(containerNumber.trim());
      
      // Show success message based on transport type and watch status
      if (europacificRef || notificationEmails || transportType === "combined_transport") {
        const msg = transportType === "combined_transport" 
          ? "Container tracked and added to watch list (Sea + Rail tracking)" 
          : "Container tracked and notifications configured!";
        toast.success(msg);
      }
      
      // Check if arrived at destination
      if (destinationPort && response.data) {
        const location = (response.data.location || "").toUpperCase();
        const pod = (response.data.port_of_discharge || "").toUpperCase();
        const status = (response.data.status || "").toLowerCase();
        const destUpper = destinationPort.toUpperCase();
        
        const arrivedStatuses = ["arrived", "discharged", "pickup", "delivered", "gate out"];
        const locationMatch = location.includes(destUpper) || pod.includes(destUpper);
        const statusMatch = arrivedStatuses.some(s => status.includes(s));
        
        if (locationMatch && statusMatch) {
          setArrivedAtDestination(true);
          toast.success(`Container arrived at ${destinationPort}! Tracking complete.`);
        } else {
          toast.success("Container found!");
        }
      } else {
        toast.success("Container found!");
      }
    } catch (error) {
      if (error.response?.status === 404) {
        const errorDetail = error.response.data.detail;
        // Handle both old string format and new object format
        if (typeof errorDetail === 'object') {
          setError({
            message: errorDetail.message,
            carrier: errorDetail.carrier,
            directUrl: errorDetail.direct_tracking_url
          });
        } else {
          setError({ message: errorDetail || "Container not found. Our team has been notified and will provide manual tracking shortly." });
        }
        toast.info("Container not found. We'll track it manually for you.");
      } else {
        toast.error("Failed to track container. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || "";
    if (s.includes("transit") || s.includes("loaded") || s.includes("departed")) return "text-primary";
    if (s.includes("arrived") || s.includes("discharged") || s.includes("pickup")) return "text-success";
    if (s.includes("pending") || s.includes("customs")) return "text-warning";
    return "text-muted-foreground";
  };

  const getStatusBadgeClass = (status) => {
    const s = status?.toLowerCase() || "";
    if (s.includes("transit") || s.includes("loaded") || s.includes("departed")) return "status-info";
    if (s.includes("arrived") || s.includes("discharged") || s.includes("pickup")) return "status-success";
    if (s.includes("pending") || s.includes("customs")) return "status-warning";
    return "bg-secondary text-muted-foreground";
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-20 pb-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {/* WhatsApp Threat Center — staff only; auto-hides for customers */}
          {user?.user_type === "europacific_staff" && (
            <div className="mb-6" data-testid="dashboard-threat-widget">
              <WhatsAppThreatWidget compact autoRefreshSec={60} />
            </div>
          )}
          {/* Header with Tabs */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
              FCL Container Tracking
            </h1>
            
            {/* Tabs */}
            <div className="flex items-center gap-2 border-b border-border">
              <button
                onClick={() => setActiveTab("track")}
                className={`px-4 py-2 font-medium text-sm transition-colors relative ${
                  activeTab === "track"
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Track Container
                </div>
                {activeTab === "track" && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                  />
                )}
              </button>
              <button
                onClick={() => setActiveTab("history")}
                className={`px-4 py-2 font-medium text-sm transition-colors relative ${
                  activeTab === "history"
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <div className="flex items-center gap-2">
                  <History className="h-4 w-4" />
                  History
                </div>
                {activeTab === "history" && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                  />
                )}
              </button>
            </div>
          </motion.div>

          {/* Tab Content */}
          {activeTab === "history" ? (
            /* History Tab Content */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center justify-between mb-6">
                <p className="text-muted-foreground">
                  View your past container tracking queries
                </p>
                <Button
                  variant="outline"
                  onClick={fetchHistory}
                  className="border-border hover:border-primary/50"
                  disabled={historyLoading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${historyLoading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </div>

              {/* History Table */}
              <div className="card-surface rounded-lg overflow-hidden">
                {historyLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : history.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">No tracking history yet</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">
                      Your tracking queries will appear here
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Container Number</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Destination</TableHead>
                        <TableHead>Last Tracked</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-mono font-medium">
                            {item.container_number}
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeClass(item.status)}`}>
                              {item.status || "Unknown"}
                            </span>
                          </TableCell>
                          <TableCell>{item.location || "-"}</TableCell>
                          <TableCell>{item.port_of_discharge || "-"}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {item.tracked_at
                              ? new Date(item.tracked_at).toLocaleDateString("en-GB", {
                                  day: "numeric",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "-"}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleTrackAgain(item.container_number)}
                            >
                              <Search className="h-4 w-4 mr-1" />
                              Track
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </motion.div>
          ) : (
            /* Track Container Tab Content */
            <>
              {/* Search Form */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card-surface p-6 mb-8"
              >
                <form onSubmit={handleSearch} className="space-y-4">
                  {/* Transport Type Selection */}
                  <div className="mb-4">
                    <Label className="text-muted-foreground text-sm mb-3 block">Transport Type</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setTransportType("port_to_port")}
                        className={`p-4 rounded-lg border-2 transition-all flex items-center gap-3 ${
                          transportType === "port_to_port"
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50"
                    }`}
                    data-testid="transport-port-to-port"
                  >
                    <Ship className={`h-6 w-6 ${transportType === "port_to_port" ? "text-primary" : "text-muted-foreground"}`} />
                    <div className="text-left">
                      <p className={`text-sm font-medium ${transportType === "port_to_port" ? "text-primary" : "text-foreground"}`}>
                        Port-to-Port
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Sea freight only
                      </p>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setTransportType("combined_transport")}
                    className={`p-4 rounded-lg border-2 transition-all flex items-center gap-3 ${
                      transportType === "combined_transport"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid="transport-combined"
                  >
                    <div className="flex items-center">
                      <Ship className={`h-5 w-5 ${transportType === "combined_transport" ? "text-primary" : "text-muted-foreground"}`} />
                      <span className={`mx-1 ${transportType === "combined_transport" ? "text-primary" : "text-muted-foreground"}`}>+</span>
                      <Train className={`h-5 w-5 ${transportType === "combined_transport" ? "text-primary" : "text-muted-foreground"}`} />
                    </div>
                    <div className="text-left">
                      <p className={`text-sm font-medium ${transportType === "combined_transport" ? "text-primary" : "text-foreground"}`}>
                        Combined Transport
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Sea + Rail (Metrans)
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 relative">
                  <Label className="text-muted-foreground text-sm mb-2 block">Container Number</Label>
                  <div className="relative">
                    <Container className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Enter container number (e.g., TLLU4322906)"
                      value={containerNumber}
                      onChange={(e) => setContainerNumber(e.target.value.toUpperCase())}
                      className="input-europacific pl-12 text-lg h-14"
                      data-testid="container-search-input"
                    />
                  </div>
                </div>
                
                <div className="lg:w-52">
                  <Label className="text-muted-foreground text-sm mb-2 block">
                    <Ship className="h-4 w-4 inline mr-1" />
                    Shipping Line
                  </Label>
                  <select
                    value={carrier}
                    onChange={(e) => setCarrier(e.target.value)}
                    className="w-full h-14 px-4 rounded-lg border border-border bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 text-foreground"
                    data-testid="carrier-select"
                  >
                    <option value="">Select carrier...</option>
                    {SHIPPING_CARRIERS.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="lg:w-52">
                  <Label className="text-muted-foreground text-sm mb-2 block">
                    <Target className="h-4 w-4 inline mr-1" />
                    {transportType === "combined_transport" ? "Port of Discharge" : "Destination Port"}
                  </Label>
                  <select
                    value={destinationPort}
                    onChange={(e) => setDestinationPort(e.target.value)}
                    className="w-full h-14 px-4 rounded-lg border border-border bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 text-foreground"
                    data-testid="destination-port-select"
                  >
                    {COMMON_PORTS.map((port) => (
                      <option key={port.code} value={port.code}>
                        {port.label}
                      </option>
                    ))}
                    <option value="">Other / Don't stop tracking</option>
                  </select>
                </div>
                
                <div className="lg:self-end">
                  <Button
                    type="submit"
                    className="btn-primary h-14 px-8 w-full lg:w-auto"
                    disabled={loading}
                    data-testid="container-search-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <Search className="h-5 w-5 mr-2" />
                        Track
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Combined Transport - Metrans Section */}
              {transportType === "combined_transport" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/20 rounded-lg p-4"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Train className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-foreground">Rail Leg (Metrans)</h3>
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">Coming Soon</span>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground text-sm mb-2 block">
                        <Train className="h-4 w-4 inline mr-1" />
                        Metrans Booking Reference
                      </Label>
                      <Input
                        type="text"
                        placeholder="e.g., MET-2026-XXXXX"
                        value={metransRef}
                        onChange={(e) => setMetransRef(e.target.value)}
                        className="input-europacific"
                        data-testid="metrans-ref-input"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-muted-foreground text-sm mb-2 block">
                        <Truck className="h-4 w-4 inline mr-1" />
                        Final Destination
                      </Label>
                      <Input
                        type="text"
                        placeholder="e.g., Ljubljana, Slovenia"
                        value={finalDestination}
                        onChange={(e) => setFinalDestination(e.target.value)}
                        className="input-europacific"
                        data-testid="final-destination-input"
                      />
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Rail tracking via Metrans will be activated once integration is configured
                  </p>
                </motion.div>
              )}
              
              {/* Europacific Reference & Notification Emails Section */}
              <div className="border-t border-border pt-4">
                <div className="flex items-center gap-2 mb-4">
                  <Mail className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground">Notification Settings</h3>
                </div>
                
                {isEuropacificUser ? (
                  /* Europacific Staff - Full Options */
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground text-sm mb-2 block">
                        <Tag className="h-4 w-4 inline mr-1" />
                        Europacific Reference
                      </Label>
                      <Input
                        type="text"
                        placeholder="e.g., EP-2026-001234"
                        value={europacificRef}
                        onChange={(e) => setEuropacificRef(e.target.value)}
                        className="input-europacific"
                        data-testid="europacific-ref-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Internal reference number for this shipment
                      </p>
                    </div>
                    
                    <div>
                      <Label className="text-muted-foreground text-sm mb-2 block">
                        <Mail className="h-4 w-4 inline mr-1" />
                        Customer Notification Emails
                      </Label>
                      <Input
                        type="text"
                        placeholder="email1@example.com, email2@example.com"
                        value={notificationEmails}
                        onChange={(e) => setNotificationEmails(e.target.value)}
                        className="input-europacific"
                        data-testid="notification-emails-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Customers will receive tracking updates at these addresses
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Customer - Limited Options */
                  <div className="bg-secondary/30 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium text-foreground">Email Notifications</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      You will receive tracking updates at: <strong>{user?.email}</strong>
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="enable-notifications"
                        defaultChecked
                        className="rounded border-border"
                      />
                      <label htmlFor="enable-notifications" className="text-sm text-muted-foreground">
                        Enable email notifications for this container
                      </label>
                    </div>
                  </div>
                )}
              </div>
              
              <p className="text-sm text-muted-foreground mt-4">
                <Target className="h-4 w-4 inline mr-1" />
                Tracking stops automatically when container arrives at your destination port
              </p>
            </form>

            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-sm text-muted-foreground">Supported carriers:</span>
              {["MAEU", "MSCU", "CMAU", "HLCU", "COSU", "EGLV", "OOLU", "YMLU"].map((prefix) => (
                <span
                  key={prefix}
                  className="font-code text-xs px-2 py-1 bg-secondary text-muted-foreground rounded"
                >
                  {prefix}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Arrived at Destination Banner */}
          {arrivedAtDestination && trackingData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-surface p-6 mb-8 border-success/30 bg-success/5"
            >
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-success" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-foreground mb-1">
                    Container Arrived at Destination
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Container <span className="font-code font-bold">{trackingData.container_number}</span> has arrived at <span className="font-bold">{destinationPort}</span>.
                    Tracking for this container is now complete.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Error State */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-surface p-6 mb-8 border-warning/30"
            >
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="h-6 w-6 text-warning" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-foreground mb-1">Manual Tracking Required</h3>
                  <p className="text-muted-foreground text-sm">{error?.message || error}</p>
                  
                  {error?.directUrl && (
                    <div className="mt-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
                      <p className="text-sm text-foreground mb-2">
                        <strong>Track directly on {error.carrier} website:</strong>
                      </p>
                      <a
                        href={error.directUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-primary hover:text-primary/80 text-sm font-medium"
                        data-testid="direct-tracking-link"
                      >
                        <Ship className="h-4 w-4" />
                        Open {error.carrier} Tracking
                        <ArrowRight className="h-4 w-4" />
                      </a>
                    </div>
                  )}
                  
                  <p className="text-sm text-primary mt-3">
                    Our operations team has been notified and will update you via email.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Tracking Results */}
          <AnimatePresence mode="wait">
            {trackingData && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
                data-testid="tracking-results"
              >
                {/* Main Status Card */}
                <div className="card-surface p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-6">
                    <div className="flex items-center gap-4">
                      <div className="h-14 w-14 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Container className="h-7 w-7 text-primary" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="font-code text-2xl font-bold text-foreground">
                          {trackingData.container_number}
                        </p>
                        <p className="text-muted-foreground">{trackingData.carrier}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <ReminderButton
                        referenceType="container"
                        referenceNumber={trackingData.container_number}
                        getAuthHeaders={getAuthHeaders}
                      />
                      <div className={`status-badge ${getStatusBadgeClass(trackingData.status)}`}>
                        <span className="h-2 w-2 rounded-full bg-current mr-2" />
                        {trackingData.status}
                      </div>
                    </div>
                  </div>

                  {/* Info Grid */}
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-secondary/50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 text-muted-foreground mb-2">
                        <MapPin className="h-4 w-4" strokeWidth={1.5} />
                        <span className="text-sm">Current Location</span>
                      </div>
                      <p className="font-heading text-lg font-semibold text-foreground">
                        {trackingData.location || "N/A"}
                      </p>
                    </div>

                    <div className="bg-secondary/50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 text-muted-foreground mb-2">
                        <Ship className="h-4 w-4" strokeWidth={1.5} />
                        <span className="text-sm">Vessel</span>
                      </div>
                      <p className="font-heading text-lg font-semibold text-foreground">
                        {trackingData.vessel_name || "N/A"}
                      </p>
                      {trackingData.voyage_number && (
                        <p className="text-sm text-muted-foreground font-code">
                          {trackingData.voyage_number}
                        </p>
                      )}
                    </div>

                    <div className="bg-secondary/50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 text-muted-foreground mb-2">
                        <Calendar className="h-4 w-4" strokeWidth={1.5} />
                        <span className="text-sm">ETA</span>
                      </div>
                      <p className="font-heading text-lg font-semibold text-foreground">
                        {trackingData.eta || "Not available"}
                      </p>
                    </div>

                    <div className="bg-secondary/50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 text-muted-foreground mb-2">
                        <Clock className="h-4 w-4" strokeWidth={1.5} />
                        <span className="text-sm">Last Update</span>
                      </div>
                      <p className="font-heading text-lg font-semibold text-foreground">
                        {new Date(trackingData.last_update).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Route Info */}
                  {(trackingData.port_of_loading || trackingData.port_of_discharge) && (
                    <div className="mt-6 pt-6 border-t border-border">
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3">
                          <Anchor className="h-5 w-5 text-primary" strokeWidth={1.5} />
                          <div>
                            <p className="text-sm text-muted-foreground">Port of Loading</p>
                            <p className="font-medium text-foreground">
                              {trackingData.port_of_loading || "N/A"}
                            </p>
                          </div>
                        </div>
                        <ArrowRight className="h-5 w-5 text-muted-foreground hidden sm:block" />
                        <div className="flex items-center gap-3">
                          <MapPin className="h-5 w-5 text-success" strokeWidth={1.5} />
                          <div>
                            <p className="text-sm text-muted-foreground">Port of Discharge</p>
                            <p className="font-medium text-foreground">
                              {trackingData.port_of_discharge || "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Events Timeline */}
                {trackingData.events && trackingData.events.length > 0 && (
                  <div className="card-surface p-6">
                    <button
                      onClick={() => setShowEvents(!showEvents)}
                      className="w-full flex items-center justify-between text-left"
                      data-testid="toggle-events-btn"
                    >
                      <h3 className="font-heading text-lg font-bold text-foreground">
                        Tracking Events ({trackingData.events.length})
                      </h3>
                      {showEvents ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </button>

                    <AnimatePresence>
                      {showEvents && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="mt-6 space-y-4"
                        >
                          {[...trackingData.events].reverse().map((event, index) => (
                            <div
                              key={index}
                              className="flex gap-4 pb-4 border-b border-border last:border-0"
                            >
                              <div className="relative">
                                <div
                                  className={`h-6 w-6 rounded-full flex items-center justify-center ${
                                    index === 0
                                      ? "bg-primary"
                                      : event.actual === false
                                      ? "bg-amber-100 border border-amber-300"
                                      : "bg-secondary border border-border"
                                  }`}
                                >
                                  <div
                                    className={`h-2 w-2 rounded-full ${
                                      index === 0 ? "bg-white" : event.actual === false ? "bg-amber-500" : "bg-muted-foreground"
                                    }`}
                                  />
                                </div>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className={`font-medium ${getStatusColor(event.status)}`}>
                                    {event.status}
                                  </p>
                                  {event.actual === false && (
                                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                      Estimated
                                    </span>
                                  )}
                                  {event.transport_type && (
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                      event.transport_type === "VESSEL" 
                                        ? "bg-blue-100 text-blue-700" 
                                        : event.transport_type === "TRUCK"
                                        ? "bg-green-100 text-green-700"
                                        : "bg-gray-100 text-gray-700"
                                    }`}>
                                      {event.transport_type}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">{event.location}</p>
                                {event.vessel_name && (
                                  <p className="text-xs text-primary mt-0.5">
                                    <Ship className="inline h-3 w-3 mr-1" />
                                    {event.vessel_name}
                                    {event.voyage && ` (${event.voyage})`}
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground font-code mt-1">
                                  {new Date(event.date).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Photos Section */}
                {photos.length > 0 && (
                  <div className="card-surface p-6">
                    <button
                      onClick={() => setShowPhotos(!showPhotos)}
                      className="w-full flex items-center justify-between text-left"
                    >
                      <h3 className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
                        <Camera className="h-5 w-5 text-accent" />
                        Shipment Photos ({photos.length})
                      </h3>
                      {showPhotos ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </button>

                    <AnimatePresence>
                      {showPhotos && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="mt-6"
                        >
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {photos.map((photo) => (
                              <div
                                key={photo.id}
                                className="relative group cursor-pointer rounded-lg overflow-hidden border border-border hover:border-accent transition-colors"
                                onClick={() => setSelectedPhoto(photo)}
                              >
                                <img
                                  src={`${API_URL}/photos/file/${photo.id}`}
                                  alt={photo.filename}
                                  className="w-full h-32 object-cover"
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <Image className="h-6 w-6 text-white" />
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-2">
                                  <p className="text-xs text-white truncate">{photo.filename}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Photo Lightbox */}
                {selectedPhoto && (
                  <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
                    <DialogContent className="bg-black border-none max-w-4xl">
                      <img
                        src={`${API_URL}/photos/file/${selectedPhoto.id}`}
                        alt={selectedPhoto.filename}
                        className="w-full max-h-[80vh] object-contain"
                      />
                      <div className="text-center mt-4">
                        <p className="text-white text-sm">{selectedPhoto.filename}</p>
                        <p className="text-gray-400 text-xs">{new Date(selectedPhoto.uploaded_at).toLocaleString()}</p>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}

                {/* AI Assistant CTA */}
                <div className="card-surface p-6 border-primary/20 bg-primary/5">
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <MessageSquare className="h-6 w-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                      <h3 className="font-heading text-lg font-bold text-foreground">
                        Need more information?
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Ask our AI assistant for detailed explanations and shipping insights
                      </p>
                    </div>
                    <Button
                      onClick={() => navigate("/chat")}
                      className="btn-primary"
                      data-testid="go-to-chat-btn"
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Ask AI Assistant
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Empty State */}
          {activeTab === "track" && !trackingData && !loading && !error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-center py-16"
            >
              <Container className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" strokeWidth={1} />
              <p className="text-muted-foreground">
                Enter a container number above to start tracking
              </p>
            </motion.div>
          )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export { DashboardPage };
